import type { ReactNode } from "react";
import { MAX_ATTEMPTS, type GuessEntry } from "@mk/shared";
import type { ShareResult } from "../features/share/shareText.ts";
import { ResultBoard } from "./ResultBoard.tsx";
import { ShareButton } from "./ShareButton.tsx";
import { WordMeaning } from "./WordMeaning.tsx";
import { Sheet } from "./Sheet.tsx";
import { messages } from "../i18n/index.ts";

interface ResultSheetProps {
  open: boolean;
  won: boolean;
  answer: string;
  rows: readonly GuessEntry[];
  onClose: () => void;
  onNewGame: () => void;
  newGameLabel?: string;
  /** Verilirse arkadaş karşılaştırma düğmesi çıkar (günlük mod). */
  onShowFriends?: () => void;
  /** Panelin en altına eklenen serbest içerik — günlükte geri sayım. */
  footer?: ReactNode;
  /** Verilirse paylaş düğmesi çıkar. */
  share?: ShareResult;
}

export function ResultSheet({
  open,
  won,
  answer,
  rows,
  onClose,
  onNewGame,
  newGameLabel = messages.result.newWord,
  onShowFriends,
  footer,
  share,
}: ResultSheetProps) {
  return (
    <Sheet
      open={open}
      title={won ? messages.result.won : messages.result.finished}
      onClose={onClose}
      // Panel ekranın ortasında durur: alta yapışınca yarım kalmış gibi
      // görünüyor ve tahtanın üstünü örtüyordu.
      placement="center"
    >
      <p className="text-sm text-muted">
        {won
          ? messages.result.solvedIn(rows.length)
          : messages.result.attemptsUsed(MAX_ATTEMPTS)}
      </p>

      {/*
       * Cevap satırı YALNIZCA bilemeyene gösteriliyor.
       *
       * Bilene de gösterildiğinde panelde aynı kelime iki kez, iki ayrı yeşil
       * satır olarak çıkıyordu: üstte cevap, altta onu bulan son tahmin. İkisi
       * aynı göründüğü için tahta bir fazla satırlı sanılıyordu.
       */}
      <div className="result-stack">
        {!won && (
          <div className="result-block">
            <span className="result-block__label">{messages.result.word}</span>
            <div className="answer-row">
              {[...answer].map((letter, i) => (
                <span key={i} className="tile" data-filled="true" data-state="correct">
                  {letter}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="result-block">
          <span className="result-block__label">{messages.result.guesses}</span>
          <ResultBoard rows={rows} />
        </div>
      </div>

      {/* Oyun bitti; cevabı zaten biliyor, anlamını da sorabilsin. */}
      <div className="mt-4">
        <WordMeaning word={answer} />
      </div>

      <div className="mt-4 flex flex-col gap-2">
        {share && <ShareButton result={share} rows={rows} spoilerRisk={share.mode === "daily"} />}
        {onShowFriends && (
          <button type="button" className="btn-primary" onClick={onShowFriends}>
            {messages.result.compareFriends}
          </button>
        )}
        <button
          type="button"
          className={onShowFriends ? "btn-secondary" : "btn-primary"}
          onClick={onNewGame}
        >
          {newGameLabel}
        </button>
      </div>

      {footer && <div className="mt-4">{footer}</div>}
    </Sheet>
  );
}
